import React, { useEffect, useMemo, useState } from 'react';
import { FiDollarSign, FiClock } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import styles from './InvestmentCalculator.module.css';

interface InvestmentCalculatorProps {
  blockPrice?: number | null;
  minBlocks?: number | null;
  maxBlocks?: number | null;
  termMonths?: number | null;
  investmentEndAt?: string | Date | null;
}

const MXN_TO_USD_RATE = 0.058;

const clampNumber = (n: number, min: number, max: number) => {
  if (!Number.isFinite(n)) return min;
  return Math.min(max, Math.max(min, n));
};

const InvestmentCalculator: React.FC<InvestmentCalculatorProps> = ({
  blockPrice,
  minBlocks,
  maxBlocks,
  termMonths,
  investmentEndAt
}) => {
  const { t, i18n } = useTranslation();

  const [blocksQty, setBlocksQty] = useState<number>(1);
  const [manualAmount, setManualAmount] = useState<number>(100000);
  const [manualTermMonths, setManualTermMonths] = useState<number>(12);

  const isSpanish = i18n.language.startsWith('es');
  const locale = isSpanish ? 'es-MX' : 'en-US';
  const currency = isSpanish ? 'MXN' : 'USD';

  const formatCurrency = (amountMXN: number) => {
    const finalAmount = isSpanish ? amountMXN : amountMXN * MXN_TO_USD_RATE;
    return finalAmount.toLocaleString(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 0
    });
  };

  const safeBlockPrice = Number.isFinite(Number(blockPrice)) ? Number(blockPrice) : 0;
  const safeMinBlocks = Number.isFinite(Number(minBlocks)) && Number(minBlocks) > 0 ? Math.floor(Number(minBlocks)) : 1;
  const safeMaxBlocks =
    Number.isFinite(Number(maxBlocks)) && Number(maxBlocks) > 0
      ? Math.max(Math.floor(Number(maxBlocks)), safeMinBlocks)
      : safeMinBlocks + 999;
  const useBlocks = safeBlockPrice > 0;

  useEffect(() => {
    setBlocksQty((prev) => clampNumber(prev || safeMinBlocks, safeMinBlocks, safeMaxBlocks));
  }, [safeMinBlocks, safeMaxBlocks]);

  const resolvedAmount = useMemo(() => {
    if (useBlocks) {
      const qty = Number.isFinite(blocksQty) ? blocksQty : safeMinBlocks;
      return qty * safeBlockPrice;
    }
    return Number.isFinite(manualAmount) ? manualAmount : 0;
  }, [useBlocks, blocksQty, safeBlockPrice, safeMinBlocks, manualAmount]);

  const resolvedTermMonths = useMemo(() => {
    if (Number.isFinite(Number(termMonths)) && Number(termMonths) > 0) {
      return Math.max(1, Math.floor(Number(termMonths)));
    }
    return Math.max(1, Math.floor(Number.isFinite(manualTermMonths) ? manualTermMonths : 12));
  }, [termMonths, manualTermMonths]);

  const exitDateLabel = useMemo(() => {
    if (!investmentEndAt) return t('calculator.exit_date_unavailable');
    const d = investmentEndAt instanceof Date ? investmentEndAt : new Date(investmentEndAt);
    if (Number.isNaN(d.getTime())) return t('calculator.exit_date_unavailable');
    return d.toLocaleDateString(locale);
  }, [investmentEndAt, locale, t]);

  return (
    <div className={styles.calculatorContainer}>
      <h3 className={styles.title}>{t('calculator.title')}</h3>

      {useBlocks ? (
        <div className={styles.controlGroup}>
          <label htmlFor="blocks" className={styles.label}>
            <FiDollarSign />
            {t('calculator.blocks_qty')}
          </label>

          <input
            type="number"
            id="blocks"
            value={Number.isFinite(blocksQty) ? blocksQty : safeMinBlocks}
            onChange={e => setBlocksQty(clampNumber(Number(e.target.value), safeMinBlocks, safeMaxBlocks))}
            min={safeMinBlocks}
            max={safeMaxBlocks}
            step={1}
            className={styles.input}
          />

          <span className={styles.inputHint}>
            {t('calculator.minimum')} {safeMinBlocks} | {t('calculator.maximum')} {safeMaxBlocks}
          </span>
        </div>
      ) : (
        <div className={styles.controlGroup}>
          <label htmlFor="amount" className={styles.label}>
            <FiDollarSign />
            {t('calculator.amount')}
          </label>

          <input
            type="number"
            id="amount"
            value={Number.isFinite(manualAmount) ? manualAmount : 0}
            onChange={e => setManualAmount(clampNumber(Number(e.target.value), 0, 1_000_000_000))}
            min={10000}
            step={5000}
            className={styles.input}
          />

          <span className={styles.inputHint}>
            {t('calculator.minimum')} {formatCurrency(10000)}
          </span>
        </div>
      )}

      {!termMonths ? (
        <div className={styles.controlGroup}>
          <label htmlFor="period" className={styles.label}>
            <FiClock />
            {t('calculator.period')}
          </label>

          <input
            type="number"
            id="period"
            value={Number.isFinite(manualTermMonths) ? manualTermMonths : 1}
            onChange={e => setManualTermMonths(clampNumber(Number(e.target.value), 1, 120))}
            min={1}
            max={120}
            className={styles.input}
          />
        </div>
      ) : null}

      <div className={styles.metaRow}>
        <div className={styles.metaItem}>
          <span className={styles.metaLabel}>{t('calculator.project_term')}</span>
          <strong className={styles.metaValue}>{resolvedTermMonths} {t('calculator.months')}</strong>
        </div>
        <div className={styles.metaItem}>
          <span className={styles.metaLabel}>{t('calculator.exit_date')}</span>
          <strong className={styles.metaValue}>{exitDateLabel}</strong>
        </div>
      </div>

      <div className={styles.metaRow}>
        <div className={styles.metaItem}>
          <span className={styles.metaLabel}>{t('calculator.block_price')}</span>
          <strong className={styles.metaValue}>{safeBlockPrice > 0 ? formatCurrency(safeBlockPrice) : '—'}</strong>
        </div>
        <div className={styles.metaItem}>
          <span className={styles.metaLabel}>{t('calculator.estimated_amount')}</span>
          <strong className={styles.metaValue}>{formatCurrency(resolvedAmount)}</strong>
        </div>
      </div>
    </div>
  );
};

export default InvestmentCalculator;
